import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Clock } from 'lucide-react';
import { quickReads, quickReadCategories } from '../data/mock';

const QuickReads = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredQuickReads = useMemo(() => {
    if (selectedCategory === 'All') return quickReads;
    return quickReads.filter((item) => item.category === selectedCategory);
  }, [selectedCategory]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 gradient-hero" />
        <div className="absolute inset-0 pattern-bg" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="icon-container w-12 h-12 rounded-xl flex items-center justify-center">
              <Zap className="h-6 w-6 text-primary" />
            </div>
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Executive Briefs</span>
          </div>
          
          <h1 className="font-display text-4xl lg:text-5xl xl:text-6xl text-foreground mb-6">
            Quick Reads
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground max-w-2xl">
            One-page insights for busy executives. Get the key takeaways in under a minute.
          </p>
        </div>
      </section>

      {/* Category Filters */}
      <section className="bg-card border-b border-border sticky top-28 lg:top-32 z-40 shadow-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5">
          <div className="flex flex-wrap gap-2">
            {quickReadCategories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-smooth ${
                  selectedCategory === category
                    ? 'btn-primary'
                    : 'bg-secondary text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Reads Grid */}
      <section className="bg-background relative">
        <div className="absolute inset-0 pattern-bg opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-10 lg:py-14">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredQuickReads.map((item) => (
              <Link
                key={item.id}
                to={`/quick-read/${item.id}`}
                className="feature-card rounded-2xl overflow-hidden group"
              >
                {/* Image */}
                <div className="newsletter-image h-48 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="category-badge">
                      {item.category}
                    </span>
                    <span className="flex items-center text-xs text-muted-foreground bg-secondary px-3 py-1 rounded-full">
                      <Clock className="h-3 w-3 mr-1" />
                      {item.readTime}
                    </span>
                  </div>
                  
                  <h3 className="font-display text-lg text-foreground mb-2 group-hover:text-primary transition-smooth">
                    {item.title}
                  </h3>
                  
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                    {item.summary}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          {filteredQuickReads.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-6">
                <Zap className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-lg mb-2">
                No quick reads found in this category.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default QuickReads;
